import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import pdf from '../../assets/images/file-pdf-solid.svg';
import csv from '../../assets/images/icons8-excel.svg';
import { useAuth } from '../../hooks/customers/AuthProvider';
import API from "../../api/client";
import * as ENDPOINTS from "../../api/endpoints";
import Select from "react-select";
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { LoaderContext } from "../utils/Contexts";
import { DateRangePicker, Stack } from 'rsuite';
import { components } from 'react-select';
import * as XLSX from 'xlsx';

const customStyles = {
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#07888d'
      : state.isFocused
        ? '#e8f4f5'
        : '#fff',
    color: state.isSelected ? '#fff' : '#045d61',
    cursor: 'pointer',
  }),

  control: (provided) => ({
    ...provided,
    borderColor: '#b9dfe1',
    boxShadow: 'none',
    minHeight: '45px',
    padding: '4px 8px',
    '&:hover': {
      borderColor: '#07888d',
    },
  }),

  input: (provided) => ({
    ...provided,
    margin: '4px 0',
    padding: '4px 6px',
    color: '#045d61',
    fontSize: '14px',
  }),

  menu: (provided) => ({
    ...provided,
    backgroundColor: '#fff',
    border: '1px solid #b9dfe1',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  }),

  menuList: (provided) => ({
    ...provided,
    maxHeight: '250px',
  }),
};

const Option = (props) => (
  <components.Option {...props}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <input
        type="checkbox"
        checked={props.isSelected}
        onChange={() => props.selectOption(props.data)}
        style={{
          cursor: 'pointer',
          flexShrink: 0,
          width: '16px',
          height: '16px',
          accentColor: '#07888d !important',
        }}
      />
      <span>{props.data.label}</span>
    </div>
  </components.Option>
);

const MultiValue = (props) => {
  const label = props.data.label;
  const truncatedLabel = label.length > 15 ? `${label.substring(0, 15)}...` : label;

  return (
    <components.MultiValue {...props}>
      <div style={{
        padding: '6px 10px',
        backgroundColor: '#e8f4f5',
        borderRadius: '4px',
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: '13px',
        gap: '4px',
        // whiteSpace: 'nowrap',
      }}>
        <span title={label}>{truncatedLabel}</span>
      </div>
    </components.MultiValue>
  );
};


const ValueContainer = ({ children, ...props }) => {
  const values = props.getValue();

  return (
    <components.ValueContainer {...props}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "6px",
          width: "100%",
          maxHeight: "120px",
          overflowY: values.length > 2 ? "auto" : "visible",
          padding: "2px 0",
          minWidth: 0,
        }}
      >
        {children}
      </div>
    </components.ValueContainer>
  );
};

const columnWidths = {
  category: 180,
  product: 480,
  unit: 50,
  qty: 80,
  price: 100,
  total: 120
};


function Reportdatatable({ setCategoryChartData, setProductCountByCategory }) {

  const pickerRef = useRef(null);
  const popupRef = useRef(null);
  const branchInitializedRef = useRef(false);
  const [active, setActive] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [filters, setFilters] = useState({
    branch: '',
    date: {
      start: '',
      end: '',
    },
  });
  const auth = useAuth();
  const customerId = auth.customer.id;
  const customer = auth.customer;
  const customerSpecialPrices = customer?.customerSpecialPrices;
  const [branches, setBranches] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [invoice, setInvoice] = useState([]);
  const [startDate, setStartDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const { isLoader, setLoader } = useContext(LoaderContext);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [range, setRange] = useState(null);


  // ✅ Get selected branch names
  const branchNames = React.useMemo(() => {
    const names = new Set();

    products.forEach(row => {
      if (row.branches) {
        Object.keys(row.branches).forEach(branch => {
          names.add(branch);
        });
      }
    });

    return Array.from(names);
  }, [products]);

  const [sorting, setSorting] = useState([]);

  const columns = useMemo(() => {
    const productColumns = [
      {
        accessorKey: 'category_name',
        header: 'Category',
        size: columnWidths.category,
        cell: info => info.getValue() || '-',
      },
      {
        accessorKey: 'product_name',
        header: 'Product Name',
        size: columnWidths.product,
        cell: info => (
          <div
            style={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              lineHeight: '18px',
            }}
          >
            {info.getValue()}
          </div>
        ),
      },
      {
        accessorKey: 'product_unit',
        header: 'Unit',
        size: columnWidths.unit,
        cell: info => (
          <div style={{ textAlign: "center" }}>
            {info.getValue()}
          </div>
        ),
      },
      {
        accessorKey: 'product_price',
        header: 'Unit Price($)',
        size: 70,
        minSize: 70,
        maxSize: 70,
        cell: info => {
          const row = info.row.original;

          let basePrice = parseFloat(
            row.product_price?.$numberDecimal || 0
          );

          let finalPrice = basePrice;

          const special = customerSpecialPrices?.[row.product_code];

          if (special?.price?.$numberDecimal) {
            finalPrice = parseFloat(
              special.price.$numberDecimal
            );
          }

          return (
            <div style={{ textAlign: "center" }}>
              {info.getValue()}
            </div>
          );
        },
      },
    ];

    const branchColumns = branchNames.map(branch => ({
      id: `branch_${branch}`,
      header: (
        <div title={branch} style={{ cursor: 'help' }}>
          {branch.length > 10 ? `${branch.substring(0, 10)}...` : branch}
        </div>
      ),
      columns: [
        {
          accessorFn: row => row.branches?.[branch]?.qty || 0,
          id: `${branch}_qty`,
          header: 'Qty',
          size: columnWidths.qty,
        },
        {
          accessorFn: row =>
            parseFloat(
              row.branches?.[branch]?.gtotal_spent || 0
            ),
          id: `${branch}_spent`,
          header: 'Spent ($)',
          size: columnWidths.price,
          cell: info => `$${info.getValue().toFixed(2)}`,
        },
      ],
    }));

    return [
      {
        header: 'Product Details',
        columns: productColumns,
      },
      ...branchColumns,
    ];
  }, [branchNames, customerSpecialPrices]);

  const getBranches = async () => {
    const url = `${ENDPOINTS.GET_BRANCHES}/${customerId}`;
    const response = await API.postWithToken(url);
    if (response.statusText === 'OK') {
      const branchOptions = response?.data?.data.items.map(branch => ({
        value: branch.id,
        label: branch.company,
      }));
      const allOption = { value: '', label: 'All' };
      setBranches([allOption, ...branchOptions]);
    }
  }

  const getCategories = async () => {
    const url = `${ENDPOINTS.GET_CATEGORIES}?limit=100`;
    const response = await API.getWithToken(url);
    if (response.statusText === 'OK') {
      const categoryOptions = response?.data?.data.categories.map(category => ({
        value: category.id,
        label: category.name,
      }));
      const allOption = { value: '', label: 'All' };
      setCategories([allOption, ...categoryOptions]);
    }
  }

  const getProductsBasedCategory = async (company, category) => {
    setLoader(true);
    const url = `${ENDPOINTS.GET_PRODUCT_BASED_CATEGORY}`;

    const requestBody = {
      id: customerId,
    };

    if (company && company.length > 0) {
      const selectedCompanies = company
        .filter(branch => branch.label !== 'All')
        .map(branch => branch.label);

      if (selectedCompanies.length > 0) {
        requestBody.company = selectedCompanies;
      }
    }

    if (category && category !== 'All') {
      requestBody.category = category;
    }

    const hasStart = filters?.date?.start;
    const hasEnd = filters?.date?.end;

    if (hasStart && hasEnd) {
      requestBody.start_date = hasStart;
      requestBody.end_date = hasEnd;
    }

    const response = await API.postWithToken(url, requestBody);
    if (response.statusText === 'OK') {
      const items = response?.data?.data.items.data;
      const filtered = items.filter(row => {
        const cat = row.category_name?.trim();
        return cat && cat.length > 0;
      });
      console.log("Fetched Products:", filtered);
      setProducts(filtered);
      setHasLoaded(true);
      setLoader(false);
    }
  }
  useEffect(() => {
    getBranches();
    getCategories();
  }, []);

  useEffect(() => {
    if (!branchInitializedRef.current) return;

    const hasStart = filters?.date?.start;
    const hasEnd = filters?.date?.end;

    // wait until both dates selected
    if ((hasStart && !hasEnd) || (!hasStart && hasEnd)) {
      return;
    }

    getProductsBasedCategory(
      selectedBranch,
      selectedCategory?.label
    );
  }, [
    selectedBranch,
    selectedCategory,
    filters.date.start,
    filters.date.end,
  ]);


  const handleChange = (e) => {
    const { id, value, type, checked } = e.target;
    setFilters(prev => ({
      ...prev,
      [id]: type === 'checkbox' ? checked : value,
    }));
  };

  const filteredData = products.filter((row) => {

    const normalize = str => str?.toString().trim().toLowerCase();

    const matchBranch =
      !selectedBranch || selectedBranch.value === '' ||
      (normalize(row.branch) === normalize(selectedBranch.label));

    const matchCategory =
      !selectedCategory || selectedCategory.value === '' ||
      (row.category_name && row.category_name.toLowerCase().includes(selectedCategory.label.toLowerCase()));

    const start = fromDate;
    const end = toDate;
    const matchDate = true;

    // const matchDate = !start || !end
    //   ? true
    //   : (() => {
    //     const rowDate = new Date(row.delivery_date);
    //     rowDate.setHours(0, 0, 0, 0);
    //     start.setHours(0, 0, 0, 0);
    //     end.setHours(0, 0, 0, 0);
    //     return rowDate >= start && rowDate <= end;
    //   })();


    return matchDate && matchCategory && matchBranch;
  });

  const paginatedData = useMemo(() => {
    return filteredData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);
  }, [filteredData, currentPage, rowsPerPage]);

  const table = useReactTable({
    data: paginatedData,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const handleBranchChange = (selected) => {
    // If nothing selected, clear all
    if (!selected || selected.length === 0) {
      setSelectedBranch([]);
      return;
    }

    // Check if "All" (value === '') is in the currently selected array
    const hasAll = selected.some(option => option.value === '');

    // Check if "All" was previously selected
    const hadAll = selectedBranch.some(option => option.value === '');

    if (hasAll && !hadAll) {
      // "All" was just checked - select all branches
      setSelectedBranch(branches);
    } else if (!hasAll && hadAll) {
      // "All" was just unchecked - remove "All" and keep only individual selections
      const individualBranches = selected.filter(option => option.value !== '');
      if (individualBranches.length === 0) {
        // If no individual branches, clear everything
        setSelectedBranch([]);
      } else {
        // Keep only the individual branches that are selected
        setSelectedBranch(individualBranches);
      }
    } else if (hasAll && hadAll) {
      // Both "All" and individual branches are selected, remove "All" and keep individual
      const individualBranches = selected.filter(option => option.value !== '');
      if (individualBranches.length === 0) {
        // If clicking "All" when it's already selected, deselect all
        setSelectedBranch([]);
      } else {
        // Keep individual selections without "All"
        setSelectedBranch(individualBranches);
      }
    } else {
      // Individual branches selected without "All"
      setSelectedBranch(selected);
    }
  };

  useEffect(() => {
    if (
      branches.length > 0 &&
      categories.length > 0 &&
      !branchInitializedRef.current
    ) {
      branchInitializedRef.current = true;

      setSelectedBranch(branches);
      setSelectedCategory(categories[0]);
    }
  }, [branches, categories]);

  const handleCategoryChange = (selected) => {
    setSelectedCategory(selected);
  };

  const flattenColumns = (cols) => {
    return cols.flatMap(col => {
      if (col.columns) {
        return col.columns;
      }
      return col;
    });
  };

  const getCellValue = (col, row) => {
    // Unit
    if (col.accessorKey === "product_unit") {
      return row.product_unit || "";
    }

    // Unit Price
    if (col.accessorKey === "product_price") {
      let basePrice = parseFloat(
        row.product_price?.$numberDecimal || 0
      );

      let finalPrice = basePrice;

      const special =
        customerSpecialPrices?.[row.product_code];

      if (special?.price?.$numberDecimal) {
        finalPrice = parseFloat(
          special.price.$numberDecimal
        );
      }

      return finalPrice.toFixed(2);
    }

    // Other columns
    if (col.accessorKey) {
      return row[col.accessorKey];
    }

    if (col.accessorFn) {
      return col.accessorFn(row);
    }

    return "";
  };

  // Helper function
  const getExportHeaders = () => {
    const headers = [];
    const flatCols = flattenColumns(columns);

    flatCols.forEach(col => {
      // Product Details columns
      if (col.accessorKey) {
        const headerLabel = col.header || col.accessorKey;
        headers.push(headerLabel);
      }
      // Branch Qty columns
      else if (col.id?.endsWith('_qty')) {
        const branchName = col.id.replace(/_qty$/, '');
        headers.push(`${branchName} - Qty`);
      }
      // Branch Spent columns
      else if (col.id?.endsWith('_spent')) {
        const branchName = col.id.replace(/_spent$/, '');
        headers.push(`${branchName} - Spent ($)`);
      }
    });

    return headers;
  };

  // Flatten columns for row values
  const flatCols = columns.flatMap(col => {
    if (col.columns) {
      return col.columns;
    }
    return col;
  });


  const handleExportCSV = () => {
    const flatCols = flattenColumns(columns);
    const wsData = [];
    const merges = [];
    const headerRow1 = [];
    const headerRow2 = [];
    let colIdx = 0;

    columns.forEach(col => {
      if (col.header === 'Product Details') {
        headerRow1.push('Product Details');
        for (let i = 1; i < col.columns.length; i++) headerRow1.push('');
        merges.push({ s: { r: 0, c: colIdx }, e: { r: 0, c: colIdx + col.columns.length - 1 } });
        col.columns.forEach(sub => {
          headerRow2.push(typeof sub.header === 'string' ? sub.header : sub.accessorKey);
        });
        colIdx += col.columns.length;
      } else if (col.columns) {
        const branchName = col.id?.replace('branch_', '') || '';
        headerRow1.push(branchName, '');
        headerRow2.push('Qty', 'Spent ($)');
        merges.push({ s: { r: 0, c: colIdx }, e: { r: 0, c: colIdx + 1 } });
        colIdx += 2;
      }
    });

    wsData.push(headerRow1, headerRow2);

    filteredData.forEach(row => {
      const dataRow = flatCols.map(col => {
        let value;

        if (col.accessorKey) {
          if (col.accessorKey === 'product_price') {
            value = parseFloat(
              row.product_price?.$numberDecimal || row.product_price || 0
            ).toFixed(2);
          } else {
            value = row[col.accessorKey];
          }
          if (col.accessorKey === 'product_unit') value = value || '-';
        } else if (col.id?.endsWith('_qty')) {
          const bn = col.id.replace(/_qty$/, '');
          value = row.branches?.[bn]?.qty ?? 0;
        } else if (col.id?.endsWith('_spent')) {
          const bn = col.id.replace(/_spent$/, '');
          value = parseFloat(row.branches?.[bn]?.gtotal_spent || 0).toFixed(2);
        } else if (col.accessorFn) {
          const v = col.accessorFn(row);
          value = typeof v === 'number' ? v.toFixed(2) : v;
        }

        return value ?? 0;
      });
      wsData.push(dataRow);
    });

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!merges'] = merges;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Product Analysis');
    XLSX.writeFile(wb, 'product_analysis_report.xlsx');
  };

  const handleExportPDF = () => {
    const requiredWidth = 355 + branchNames.length * 82 + 40;
    const pageHeight = 842;
    const pageWidth = Math.max(requiredWidth, 1191);

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: [pageHeight, pageWidth],
    });

    const flatCols = flattenColumns(columns);

    // ---------- HEADER ROWS ----------
    const headRow1 = [];
    const headRow2 = [];

    columns.forEach((col) => {
      // Product Details group
      if (col.header === "Product Details") {
        headRow1.push({
          content: "Product Details",
          colSpan: col.columns.length,
          styles: {
            halign: "center",
            fillColor: [110, 191, 194],
            textColor: [255, 255, 255],
            fontStyle: "bold",
          },
        });

        col.columns.forEach((sub) => {
          headRow2.push({
            content:
              typeof sub.header === "string"
                ? sub.header
                : sub.accessorKey,
            styles: {
              halign: "center",
              fillColor: [232, 244, 245],
              textColor: [7, 136, 141],
              fontStyle: "bold",
            },
          });
        });
      }

      // Branch groups
      else if (col.columns) {
        const branchName = col.id?.replace("branch_", "") || "";

        headRow1.push({
          content: branchName,
          colSpan: col.columns.length,
          styles: {
            halign: "center",
            fillColor: [110, 191, 194],
            textColor: [255, 255, 255],
            fontStyle: "bold",
          },
        });

        col.columns.forEach((sub) => {
          headRow2.push({
            content: sub.header,
            styles: {
              halign: "center",
              fillColor: [232, 244, 245],
              textColor: [7, 136, 141],
              fontStyle: "bold",
            },
          });
        });
      }
    });

    // ---------- BODY ----------
    const body = filteredData.map((row) => {
      return flatCols.map((col) => {
        let value = "";

        // accessorKey
        if (col.accessorKey) {
          value = row[col.accessorKey];

          // Product Price
          if (col.accessorKey === "product_price") {
            value = parseFloat(
              row.product_price?.$numberDecimal ||
              row.product_price ||
              0
            ).toFixed(2);
          }
          if (col.accessorKey === "product_unit") {
            value = value || "-";
          }
        }

        // accessorFn
        else if (col.accessorFn) {
          value = col.accessorFn(row);

          if (typeof value === "number") {
            value = col.id?.endsWith("_qty")
              ? String(value)
              : value.toFixed(2);
          }
        }

        return value ?? "-";
      });
    });

    // ---------- COLUMN STYLES ----------
    const columnStyles = {};

    flatCols.forEach((col, index) => {
      // Match React table widths
      if (col.accessorKey === "category_name") {
        columnStyles[index] = {
          cellWidth: 120,
          halign: "left",
        };
      }

      else if (col.accessorKey === "product_name") {
        columnStyles[index] = {
          cellWidth: 140,
          halign: "left",
          overflow: "linebreak",
        };
      }

      else if (col.accessorKey === "product_unit") {
        columnStyles[index] = {
          cellWidth: 50,
          halign: "center",
        };
      }

      else if (col.accessorKey === "product_price") {
        columnStyles[index] = {
          cellWidth: 45,
          halign: "right",
        };
      }

      else if (col.id?.includes("_qty")) {
        columnStyles[index] = {
          cellWidth: 40,
          halign: "center",
        };
      }

      else if (col.id?.includes("_spent")) {
        columnStyles[index] = {
          cellWidth: 42,
          halign: "right",
        };
      }
    });

    // ---------- TABLE ----------
    doc.autoTable({
      head: [headRow1, headRow2],
      body,

      startY: 40,

      styles: {
        fontSize: 6,
        cellPadding: 3,
        valign: "middle",
        overflow: "linebreak",
        lineColor: [220, 220, 220],
        lineWidth: 0.5,
      },

      bodyStyles: {
        textColor: [50, 50, 50],
      },

      alternateRowStyles: {
        fillColor: [249, 249, 249],
      },

      columnStyles,

      theme: "grid",

      // IMPORTANT
      tableWidth: "auto",

      didParseCell: function (data) {
        if (
          data.column.index >= 0 &&
          flatCols[data.column.index]?.id?.includes("_qty")
        ) {
          data.cell.styles.halign = "center";
        }

        if (
          flatCols[data.column.index]?.id?.includes("_spent") ||
          flatCols[data.column.index]?.accessorKey === "product_price"
        ) {
          data.cell.styles.halign = "right";
        }
      },
    });

    doc.save("product_analysis_report.pdf");
  };

  useEffect(() => {
    if (filters.date) {
      setSelectedDate(new Date(filters.date));
    }
  }, [filters.date]);

  const chartData = Object.values(
    filteredData
      .filter(row => {
        const cat = row.category_name?.trim();
        return cat && cat !== 'Unknown';
      })
      .reduce((acc, row) => {
        const category = row.category_name?.trim();
        const spent = parseFloat(row.total_spent?.$numberDecimal || row.total_spent || 0);
        if (!acc[category]) {
          acc[category] = { category, total: 0 };
        }
        acc[category].total += spent;
        return acc;
      }, {})
  );

  const prevDataRef = useRef([]);

  const isDifferent =
    prevDataRef.current.length !== chartData.length ||
    JSON.stringify(prevDataRef.current) !== JSON.stringify(chartData);

  if (isDifferent) {
    prevDataRef.current = chartData;
    setCategoryChartData(filteredData.length > 0 ? chartData : []);
  }


  const productCounts = Object.values(
    filteredData
      .filter(row => {
        const cat = row.category_name?.trim();
        return cat && cat !== 'Unknown';
      })
      .reduce((acc, row) => {
        const category = row.category_name;
        if (!acc[category]) {
          acc[category] = { category, count: 0 };
        }
        acc[category].count += 1;
        return acc;
      }, {})
  );

  const prevCountRef = useRef([]);

  const isCountDifferent =
    prevCountRef.current.length !== productCounts.length ||
    JSON.stringify(prevCountRef.current) !== JSON.stringify(productCounts);

  if (isCountDifferent) {
    prevCountRef.current = productCounts;
    setProductCountByCategory(filteredData.length > 0 ? productCounts : []);
  }

  return (
    <div className={`reporttables reporttables-analysis ${filteredData.length === 0 ? 'empty' : ''}`}>
      <div className="filters">
        <div className="divtabe1">
          <label className="labels">Product Category</label>
          <Select
            options={categories}
            value={selectedCategory}
            onChange={handleCategoryChange}
            classNamePrefix="custom-select"
          />
        </div>
        <div className="divtabe1">
          <label className="labels">Branch</label>
          <Select
            options={branches}
            value={selectedBranch}
            onChange={handleBranchChange}
            classNamePrefix="custom-select"
            isMulti
            components={{ Option, MultiValue, ValueContainer, Input: components.Input }}
            isSearchable
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            isClearable={false}
            styles={customStyles}
            menuPlacement="auto"
            maxMenuHeight={250}
            controlShouldRenderValue={false}

          />
        </div>
        <div className="divtabe1">
          <label className="labels">From Date</label>
          <DatePicker
            selected={fromDate}
            onChange={(date) => {
              setFromDate(date);
              setToDate(null);
              const formatted = date ? date.toLocaleDateString('en-CA') : '';
              setFilters(prev => ({
                ...prev,
                date: {
                  ...prev.date,
                  start: formatted,
                }
              }));
            }}
            placeholderText=""
            dateFormat="dd/MM/yyyy"
            className="form-control"
          />
        </div>

        <div className="divtabe1">
          <label className="labels">To Date</label>
          <DatePicker
            selected={toDate}
            onChange={(date) => {
              setToDate(date);
              const formatted = date ? date.toLocaleDateString('en-CA') : '';
              setFilters(prev => ({
                ...prev,
                date: {
                  ...prev.date,
                  end: formatted,
                }
              }));
            }}
            placeholderText=""
            dateFormat="dd/MM/yyyy"
            className="form-control"
            minDate={fromDate}
            disabled={!fromDate}
          />
        </div>
        <div className="divtabe1">
          <label className="labels">Export Table To</label>
          <div className="reportexport">
            <img src={pdf} alt="files" onClick={handleExportPDF} />
            <img src={csv} alt="files" onClick={handleExportCSV} />
          </div>
        </div>
      </div>
      {hasLoaded && (
        <>
          <div style={{ width: "100%", overflowX: "auto", overflowY: "hidden", scrollBehavior: "smooth" }}>
            <table style={{
              width: "100%",
              minWidth: "1200px",
              borderCollapse: "collapse",
            }}>
              {table.getRowModel().rows.length > 0 && (
                <thead>
                  {table.getHeaderGroups().map((headerGroup, headerIndex) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                        <th
                          key={header.id}
                          colSpan={header.colSpan}
                          style={{
                            width: header.getSize(),
                            minWidth: header.getSize(),
                            maxWidth: header.getSize(),
                            padding: "6px 4px",
                            textAlign: "center",
                            //backgroundColor: "#f5f5f5",
                            borderBottom: "1px solid #ddd",
                            borderRight: "1px solid #ddd",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            // color: "#07888d",
                            fontFamily: "Dosis, sans-serif",
                            backgroundColor:
                              headerIndex === 0
                                ? "rgb(110 191 194)"   // First header row
                                : "#e8f4f5",  // Second header row

                            color:
                              headerIndex === 0
                                ? "#fff"
                                : "#07888d",
                          }}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
              )}
              <tbody>
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={columns.length}
                      style={{
                        width: "100%",
                        minHeight: "140px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "8px",
                        marginTop: "10px",
                        color: "#333",
                        fontSize: "16px",
                        fontWeight: "700",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                      }}
                    >
                      There are no records to display
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row, idx) => (
                    <tr
                      key={row.id}
                      style={{
                        backgroundColor: idx % 2 === 0 ? '#fff' : '#f9f9f9',
                        borderBottom: '1px solid #eee',
                      }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = idx % 2 === 0 ? '#fff' : '#f9f9f9'}
                    >
                      {row.getVisibleCells().map(cell => (
                        <td
                          key={cell.id}
                          style={{
                            width: `${cell.column.getSize()}px`,
                            minWidth: `${cell.column.getSize()}px`,
                            maxWidth: `${cell.column.getSize()}px`,
                            padding: "6px 4px",
                            textAlign: cell.column.id.includes("spent")
                              ? "right"
                              : cell.column.id.includes("qty")
                                ? "center"
                                : "left",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            borderRight: "1px solid #ddd",
                          }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {filteredData.length > 0 && (
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "20px",
              padding: "10px",
              backgroundColor: "#f9f9f9",
              borderTop: "1px solid #ddd",
            }}>
              <div className="reportsizelists">
                Showing {filteredData.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1}
                &nbsp;to&nbsp;
                {Math.min(currentPage * rowsPerPage, filteredData.length)} of {filteredData.length} entries
              </div>
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <select
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: "5px 10px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span>Rows per page</span>
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  style={{
                    padding: "5px 10px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    cursor: currentPage === 1 ? "not-allowed" : "pointer",
                    backgroundColor: currentPage === 1 ? "#f0f0f0" : "#fff",
                  }}
                >
                  Previous
                </button>
                <span>{currentPage}</span>
                <button
                  onClick={() => setCurrentPage(Math.min(Math.ceil(filteredData.length / rowsPerPage), currentPage + 1))}
                  disabled={currentPage === Math.ceil(filteredData.length / rowsPerPage)}
                  style={{
                    padding: "5px 10px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    cursor: currentPage === Math.ceil(filteredData.length / rowsPerPage) ? "not-allowed" : "pointer",
                    backgroundColor: currentPage === Math.ceil(filteredData.length / rowsPerPage) ? "#f0f0f0" : "#fff",
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>

  );
}

export default Reportdatatable;